import React, { useEffect, useState } from "react";
import { DISMISS, useNotification } from "collections/Notifications";
import { LoadingIndicator } from "elements";
import { OptionType } from "elements/DropdownList";
import { Form, Formik, setIn, useFormikContext, getIn } from "collections/Form";
import BlockingForm from "collections/Form/BlockingForm";
import {
  DeviceSettingsQuery,
  Enum_MtuMode,
  Enum_TosType,
  Enum_MonitorDisplay_Primary,
  Enum_MonitorDisplay_Secondary_Willie,
  Enum_DateFormat,
  Enum_TimeFormat,
  Enum_TimeServerMode,
  Enum_TimeZone,
  useUpsertDeviceSettingsMutation,
  Enum_CalendarReminderTime,
  Enum_SipTransportProtocol,
  Enum_SipConfigType,
  Enum_BfcpTransportProtocol,
  Enum_GateKeeperEnable,
  Enum_SipRegistrarServerType,
  Enum_ContentQualityPreference,
  Enum_MtuSize
} from "generated/graphql";
import UpdateSettings from "routes/Manage/Inventory/Settings/UpdateSoftware";
import i18next, { LocalKeys, useTranslation } from "utilities/i18n";
import * as _ from "lodash";
import { ToolTip, UpdateProgress } from "elements";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { Layout } from "views/Main/Layouts";
import {
  BasicDeviceSettings,
  deviceSettingsSchema,
  deviceSettingsSchemaFields
} from "./schemas/deviceSettingsSchema";
import { NonNullableRequired, ValueOf } from "utilities/types";
import { deepDifference, deepOmitTypename } from "utilities/objectUtils";
import {
  useDeviceDetailsReadOnly,
  useDeviceSettingsRouteMatch
} from "../View/DeviceDetailsRouter";
import { generatePath, Switch, Route } from "polyrouter";
import { needs, useGuard } from "rbac";
import { DeviceHeader } from "../DeviceHeader";
import {
  ErrorDisplay,
  FormCheckbox,
  FormInput,
  FormSelect,
  SettingsCategory
} from "../Settings-Shared/DeviceSettingsComponents";

export function getOptionsForEnum<T>(e: T): Array<ValueOf<T>> {
  return Object.values(e);
}

// minor juggling to permit SOME options to pass straight through without needing to be translated
type OptionKeys = LocalKeys | "dd_mm_yyyy" | "mm_dd_yyyy" | "yyyy_mm_dd";
export const enumToOptions = (arr: Array<OptionKeys>): Array<OptionType> => {
  return arr.map(
    opt => ({ value: opt, text: i18next.t(opt as LocalKeys) } as OptionType)
  );
};

let requirement = needs("update:device");
const DisplaySettings: React.FC = () => {
  const { t } = useTranslation();

  const userCanEdit = useGuard(requirement);

  const displayOptions = enumToOptions(
    getOptionsForEnum(Enum_MonitorDisplay_Primary)
  );

  const displayOptions2 = enumToOptions(
    getOptionsForEnum(Enum_MonitorDisplay_Secondary_Willie)
  );
  const monitor = deviceSettingsSchemaFields.video.monitor;
  return (
    <SettingsCategory title={t("DISPLAY")}>
      <div className="details grid form">
        <div className="legend">{t("MONITORS")}</div>
        <FormSelect
          options={displayOptions}
          {...monitor.ONE.display}
          disabled={!userCanEdit}
        />
        <FormSelect
          options={displayOptions2}
          {...monitor.TWO.display}
          disabled={!userCanEdit}
        />
      </div>
    </SettingsCategory>
  );
};

const generateTimeZoneOptions = (
  timezones: Array<ValueOf<Enum_TimeZone>>
): Array<OptionType> => {
  // Type Casting here is necessary to avoid complex code
  return (timezones as Array<string>)
    .map(timezone => {
      const i18nKey = `TIMEZONE_${timezone
        .replace("/", "_")
        .toUpperCase()}` as LocalKeys;
      return {
        text: i18next.t(i18nKey),
        value: timezone
      };
    })
    .filter(timezone => timezone)
    .sort((tz1, tz2) => tz1.text.localeCompare(tz2.text));
};

const DateTimeSettings: React.FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<BasicDeviceSettings>();

  const dateFormatOptions = enumToOptions(getOptionsForEnum(Enum_DateFormat));
  const timeFormatOptions = enumToOptions(getOptionsForEnum(Enum_TimeFormat));
  const timeServerOptions = enumToOptions(
    getOptionsForEnum(Enum_TimeServerMode)
  );
  const timeZoneValues = getOptionsForEnum(Enum_TimeZone);
  const timeZoneOptions = generateTimeZoneOptions(timeZoneValues);

  const local = deviceSettingsSchemaFields.device.local;
  const userCanEdit = useGuard(needs("update:device"));
  const ntpServerTypeManual =
    values?.device?.local?.ntpServer?.mode === Enum_TimeServerMode.Manual;

  return (
    <SettingsCategory title={t("DATE_AND_TIME")}>
      <div className="details grid form">
        <FormSelect
          options={dateFormatOptions}
          {...local.datetime.date.format}
          disabled={!userCanEdit}
        />
        <FormSelect
          options={timeFormatOptions}
          {...local.datetime.time.TWO4HourClock}
          disabled={!userCanEdit}
        />
        <FormCheckbox
          {...local.autoDaylightSavings.enable}
          disabled={!userCanEdit}
        />
        <FormSelect
          options={timeZoneOptions}
          {...local.timezone}
          disabled={!userCanEdit}
        />
        <FormSelect
          options={timeServerOptions}
          {...local.ntpServer.mode}
          disabled={!userCanEdit}
        />
        {ntpServerTypeManual && (
          <>
            {values?.device?.local?.ntpServer?.address?.map((_, index) => {
              const address = local.ntpServer.address.get(index);
              address.label =
                index === 0
                  ? "PRIMARY_TIME_SERVER_ADDRESS"
                  : "SECONDARY_TIME_SERVER_ADDRESS";
              return (
                <FormInput
                  key={address.id}
                  {...address}
                  disabled={!userCanEdit}
                />
              );
            })}
          </>
        )}
      </div>
    </SettingsCategory>
  );
};

const CalendarSettings: React.FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<BasicDeviceSettings>();

  const reminderIntervalOptions = enumToOptions(
    getOptionsForEnum(Enum_CalendarReminderTime)
  );
  const exchange = deviceSettingsSchemaFields.exchange;

  const userCanEdit = useGuard(needs("update:device"));
  return (
    <SettingsCategory title={t("CALENDAR")}>
      <div className="details grid form">
        <FormCheckbox {...exchange.enable} disabled={!userCanEdit} />
        <div
          className={`show-hide ${values?.exchange?.enable ? "show" : "hide"}`}
        >
          <FormInput {...exchange.auth.email} disabled={!userCanEdit} />
          <FormInput {...exchange.auth.domain} disabled={!userCanEdit} />
          <FormInput {...exchange.auth.userName} disabled={!userCanEdit} />
          <FormInput
            type="password"
            {...exchange.auth.password}
            disabled={!userCanEdit}
          />
          <FormInput {...exchange.server.url} disabled={!userCanEdit} />
          <FormSelect
            {...exchange.meeting.reminderInterval}
            options={reminderIntervalOptions}
            disabled={!userCanEdit}
          />
          <FormCheckbox
            {...exchange.meeting.reminderSound.enable}
            disabled={!userCanEdit}
          />
          <FormCheckbox
            {...exchange.showPrivateMeeting}
            disabled={!userCanEdit}
          />
        </div>
      </div>
    </SettingsCategory>
  );
};

const CallServerSettings: React.FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<BasicDeviceSettings>();

  const configOptions = enumToOptions(getOptionsForEnum(Enum_SipConfigType));

  const transportOptions = enumToOptions(
    getOptionsForEnum(Enum_SipTransportProtocol)
  );

  const bfcpTransportOptions = enumToOptions(
    getOptionsForEnum(Enum_BfcpTransportProtocol)
  );

  const registrarServerOptions = enumToOptions(
    getOptionsForEnum(Enum_SipRegistrarServerType)
  );

  const gatekeeperOptions = enumToOptions(
    getOptionsForEnum(Enum_GateKeeperEnable)
  );
  const voIpProt = deviceSettingsSchemaFields.voIpProt;

  const userCanEdit = useGuard(needs("update:device"));
  return (
    <SettingsCategory title={t("CALL_SERVER")}>
      <div className="details grid form">
        <div className="legend">{t("SIP")}</div>
        <FormCheckbox {...voIpProt.SIP.enable} disabled={!userCanEdit} />
        <div
          className={`show-hide ${
            values?.voIpProt?.SIP?.enable ? "show" : "hide"
          }`}
        >
          <FormSelect
            options={configOptions}
            {...voIpProt.SIP.serverType}
            disabled={!userCanEdit}
          />
          <div
            className={`show-hide hide-fix ${
              "Specify" === values?.voIpProt?.SIP?.serverType ? "show" : "hide"
            }`}
          >
            <FormSelect
              options={transportOptions}
              {...voIpProt.SIP.transport}
              disabled={!userCanEdit}
            />
          </div>
          <FormCheckbox
            {...voIpProt.SIP.forceConnectionReuse}
            disabled={!userCanEdit}
          />
          <FormSelect
            options={bfcpTransportOptions}
            {...voIpProt.SIP.bfcpTransportPreference}
            disabled={!userCanEdit}
          />
          <FormInput {...voIpProt.SIP.userName} disabled={!userCanEdit} />
          <FormInput {...voIpProt.SIP.auth.userId} disabled={!userCanEdit} />
          <FormInput
            type="password"
            {...voIpProt.SIP.auth.password}
            disabled={!userCanEdit}
          />
          <div
            className={`show-hide hide-fix ${
              "Specify" === values?.voIpProt?.SIP?.serverType ? "show" : "hide"
            }`}
          >
            <FormInput
              {...voIpProt.SIP.registrarServer}
              disabled={!userCanEdit}
            />
            <FormInput {...voIpProt.SIP.proxyServer} disabled={!userCanEdit} />
          </div>
          <FormSelect
            options={registrarServerOptions}
            {...voIpProt.SIP.registrarServerType}
            disabled={!userCanEdit}
          />
        </div>
        <hr />
        <div className="legend">{t("H323")}</div>
        <FormCheckbox {...voIpProt.H323.enable} disabled={!userCanEdit} />
        <div
          className={`show-hide ${
            values?.voIpProt?.H323?.enable ? "show" : "hide"
          }`}
        >
          <FormInput {...voIpProt.H323.name} disabled={!userCanEdit} />
          <FormInput {...voIpProt.H323.e164} disabled={!userCanEdit} />
          <FormSelect
            options={gatekeeperOptions}
            {...voIpProt.H323.gk.mode}
            disabled={!userCanEdit}
          />
          <div
            className={`show-hide ${
              "Specify" === values?.voIpProt?.H323?.gk?.mode ? "show" : "hide"
            }`}
          >
            <FormInput
              {...voIpProt.H323.gk.ipAddress}
              disabled={!userCanEdit}
            />
            <FormCheckbox
              {...voIpProt.H323.gk.auth.enable}
              disabled={!userCanEdit}
            />
            <div
              className={`show-hide ${
                values?.voIpProt?.H323?.gk?.auth?.enable ? "show" : "hide"
              }`}
            >
              <FormInput
                {...voIpProt.H323.gk.auth.userId}
                disabled={!userCanEdit}
              />
              <FormInput
                type="password"
                {...voIpProt.H323.gk.auth.password}
                disabled={!userCanEdit}
              />
            </div>
          </div>
        </div>
      </div>
    </SettingsCategory>
  );
};

const NetworkQualitySettings: React.FC = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<BasicDeviceSettings>();

  const qualityPreferenceOptions = enumToOptions(
    getOptionsForEnum(Enum_ContentQualityPreference)
  );

  const intServQualityOptions: Array<OptionType> = [];
  const diffServQualityOptions: Array<OptionType> = [];

  for (let i = 0; i <= 7; i++) {
    intServQualityOptions.push({ value: `${i}`, text: `${i}` });
  }

  for (let i = 0; i <= 63; i++) {
    diffServQualityOptions.push({ value: `${i}`, text: `${i}` });
  }

  const tosOptions = enumToOptions(getOptionsForEnum(Enum_TosType));
  const mtuSizeBytesOptions = enumToOptions(getOptionsForEnum(Enum_MtuSize));
  const { content, qos } = deviceSettingsSchemaFields;

  const userCanEdit = useGuard(needs("update:device"));
  return (
    <SettingsCategory title={t("NETWORK_QUALITY")}>
      <div className="details grid form">
        <FormSelect
          options={qualityPreferenceOptions}
          {...content.conference.qualityPreference}
          disabled={!userCanEdit}
        />
        <FormSelect
          options={tosOptions}
          {...qos.tosType}
          disabled={!userCanEdit}
        />
        <div
          className={`show-hide hide-fix ${
            Enum_TosType.DiffServ !== values?.qos?.tosType ? "show" : "hide"
          }`}
        >
          <FormSelect
            options={intServQualityOptions}
            {...qos.intServ.video}
            disabled={!userCanEdit}
          />
          <FormSelect
            options={intServQualityOptions}
            {...qos.intServ.audio}
            disabled={!userCanEdit}
          />
          <FormSelect
            options={intServQualityOptions}
            {...qos.intServ.fecc}
            disabled={!userCanEdit}
          />
          <FormSelect
            options={intServQualityOptions}
            {...qos.intServ.oam}
            disabled={!userCanEdit}
          />
        </div>
        <div
          className={`show-hide hide-fix ${
            "DiffServ" === values?.qos?.tosType ? "show" : "hide"
          }`}
        >
          <FormSelect
            options={diffServQualityOptions}
            {...qos.diffServ.video}
            disabled={!userCanEdit}
          />
          <FormSelect
            {...qos.diffServ.audio}
            options={diffServQualityOptions}
          />
          <FormSelect
            {...qos.diffServ.oam}
            options={diffServQualityOptions}
            disabled={!userCanEdit}
          />
        </div>
        <div
          className={`show-hide ${
            Enum_MtuMode.Specify === values?.qos?.mtuMode ? "show" : "hide"
          }`}
        >
          <FormSelect
            options={mtuSizeBytesOptions}
            {...qos.mtuSize}
            disabled={!userCanEdit}
          />
        </div>
        <FormCheckbox {...qos.LPR.enable} disabled={!userCanEdit} />
        <FormCheckbox {...qos.rsvp.enable} disabled={!userCanEdit} />
        <FormCheckbox
          {...qos.dynamicBandwidth.enable}
          disabled={!userCanEdit}
        />
      </div>
    </SettingsCategory>
  );
};

const validate = (
  values: BasicDeviceSettings,
  initialValues: NonNullableRequired<BasicDeviceSettings>
) => {
  if (values === null) {
    return "Null values?";
  }

  let errors: any = {};

  /*
   * Monitor 1 can only be manual if monitor 2 is off.
   *
   * FUTURE:
   * Monitor 1 resolution can only be set after it is set to "manual" (this must happen in a separate set config call from the one that sets it to "manual")
   * Monitor 2 resolution can not be set
   */
  // Monitor ONE can only be CHANGED to MANUAL if TWO was OFF to start with.
  if (
    // "manual" !== initialValues.video.monitor.ONE.display &&
    "manual" === values?.video?.monitor?.ONE?.display &&
    "off" !== initialValues.video.monitor.TWO.display
  ) {
    errors = setIn(
      errors,
      "video.monitor.ONE.display",
      i18next.t("ERROR_SET_MONITOR_TWO_OFF_FIRST")
    );
  }

  // Monitor TWO can only be changed to AUTO if ONE was AUTO to start with
  if (
    "auto" !== initialValues.video.monitor.ONE.display &&
    "auto" === values?.video?.monitor?.TWO?.display
  ) {
    errors = setIn(
      errors,
      "video.monitor.TWO.display",
      i18next.t("ERROR_SET_MONITOR_ONE_AUTO_FIRST")
    );
  }

  return errors;
};

export const defaultSettings = (): NonNullableRequired<BasicDeviceSettings> =>
  deviceSettingsSchema.default() as NonNullableRequired<BasicDeviceSettings>;

export const deepAssign = (target: any, source: any): any => {
  if (_.isArray(target) || _.isArray(source)) {
    return target || source;
  }
  return _.transform(
    target,
    function(acc, value, key) {
      const fromSource = source[key];
      const targetIsObject = _.isPlainObject(value);
      const sourceIsObject = _.isPlainObject(fromSource);
      //Recurse down objects
      if (value === null || value === undefined) {
        acc[key] = fromSource;
      }
      if (targetIsObject && sourceIsObject) {
        const deepValueKeys = {
          ...fromSource,
          ...(value as object)
        };
        acc[key] = deepAssign(deepValueKeys, fromSource);
      }
      return acc;
    },
    { ...source, ...target }
  );
};
export const deepInit = (
  settings: DeviceSettingsQuery["deviceSettings"]["settings"]
): NonNullableRequired<BasicDeviceSettings> => {
  return deepAssign(settings, defaultSettings()) as NonNullableRequired<
    BasicDeviceSettings
  >;
};

export const DeviceSettings: React.FC<{
  deviceSettings: DeviceSettingsQuery["deviceSettings"] | undefined;
  onUpdate: () => void;
  onLoad: () => void;
  loading: boolean;
}> = ({ deviceSettings, loading, onUpdate, onLoad }) => {
  const {
    path,
    params: { deviceId }
  } = useDeviceSettingsRouteMatch();

  const { t } = useTranslation();
  const [, submit] = useUpsertDeviceSettingsMutation();
  const [addNotification] = useNotification();
  const device = useDeviceDetailsReadOnly(deviceId);
  const [updating, setUpdating] = useState(false);

  /**
   * Purely used to tell the parent that the route has been viewed
   * so it can derive whether an update to advanced settings query
   * will be necessary.
   */
  useEffect(() => {
    onLoad();
  }, [onLoad]);
  const data = deviceSettings?.settings;

  if (loading) {
    return <LoadingIndicator />;
  }

  const initialValues: NonNullableRequired<BasicDeviceSettings> = data
    ? deepInit(data)
    : defaultSettings();
  const accessed = new Date(deviceSettings?.fetchedAt ?? "");
  const now = new Date();
  const age = now.getTime() - accessed.getTime();
  const midnight = new Date(now.toDateString());
  let fetchedAt: undefined | string = undefined;
  if (age > now.getTime() - midnight.getTime()) {
    // If age is greater than a day, show the date accessed
    fetchedAt = accessed.toDateString();
  } else if (age > 2 * 60 * 1000) {
    // Otherwise, if resp is more than 2 min old, show time of cache
    const hours = accessed.getHours();
    const minutes = accessed.getMinutes();
    fetchedAt = `${t("TODAY_AT")} ${hours > 12 ? hours - 12 : hours}:${
      minutes < 10 ? "0" + minutes : minutes
    } ${hours > 11 ? "PM" : "AM"}`;
  }

  const { Content } = Layout;

  return (
    <Formik
      name="DeviceSettings"
      customDimensions={{
        model: device?.hardwareModel,
        software_version: device?.softwareVersion
      }}
      initialValues={initialValues}
      validationSchema={deviceSettingsSchema}
      enableReinitialize={true}
      validate={values => validate(values, initialValues)}
      onSubmit={async values => {
        [
          "qos.intServ.video",
          "qos.intServ.audio",
          "qos.intServ.fecc",
          "qos.intServ.oam",
          "qos.diffServ.video",
          "qos.diffServ.audio",
          "qos.diffServ.fecc",
          "qos.diffServ.oam"
        ].forEach(path => {
          values = setIn(values, path, parseInt(getIn(values, path)));
        });
        values = deepOmitTypename(deepDifference(values, initialValues));
        if (values.device?.local?.ntpServer?.address) {
          let addressArray = ["", ""];
          for (const addressIndex in values.device?.local?.ntpServer?.address) {
            if (
              values.device?.local?.ntpServer?.address[addressIndex] ===
              undefined
            ) {
              addressArray[addressIndex] =
                initialValues.device?.local?.ntpServer?.address[addressIndex] ||
                "";
            } else {
              addressArray[addressIndex] =
                values.device?.local?.ntpServer?.address[addressIndex];
            }
          }
          values = setIn(
            values,
            "device.local.ntpServer.address",
            addressArray
          );
        }

        const { data } = await submit(
          {
            settings: values as any,
            id: deviceId
          },
          { notify: { i18nKey: "ERROR_SAVING_DEVICE_SETTINGS" } }
        );

        if (data?.upsertDeviceSettings) {
          addNotification({
            type: "success",
            message: t("DEVICE_SETTINGS_UPDATED"),
            dismiss: DISMISS.DEFAULT
          });

          onUpdate();
        }
      }}
    >
      {({ dirty }) => (
        <BlockingForm unsavedChanges={dirty} allowedSubRoutes={["/settings"]}>
          <Form className="form pb-2">
            <DeviceHeader
              device={device}
              pageTitle={
                <>
                  {device?.hardwareModel}
                  <p className="inline-block device-header-name">
                    {` | ${device?.name ||
                      device?.room?.name ||
                      device?.macAddress}`}
                  </p>
                </>
              }
              hasActionButtons
            >
              <div className="sub-header-information">
                <div className="last-checked-notification">
                  {fetchedAt ? (
                    <ToolTip
                      iconTitle={t("LAST_CHECKED")}
                      position="bottom"
                      iconReplacement={
                        <h3 className="fetched">
                          {t("LAST_CHECKED")}
                          <span>{fetchedAt}</span>
                        </h3>
                      }
                    >
                      <p>{t("ERROR_DEVICE_TIMEOUT")}</p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onUpdate}
                      >
                        {t("RETRY")}
                      </button>
                    </ToolTip>
                  ) : null}
                </div>
                <UpdateProgress
                  id={deviceId}
                  updating={updating}
                  setUpdating={setUpdating}
                />
              </div>
              <ErrorDisplay />
            </DeviceHeader>
            <Content.ScrollingList>
              <Switch>
                <Route
                  path={generatePath(path, { deviceId, category: "display" })}
                  component={DisplaySettings}
                />
                <Route
                  path={generatePath(path, { deviceId, category: "date" })}
                  component={DateTimeSettings}
                />
                <Route
                  path={generatePath(path, { deviceId, category: "calendar" })}
                  component={CalendarSettings}
                />
                <Route path={generatePath(path, { deviceId, category: "sw" })}>
                  <UpdateSettings
                    id={deviceId}
                    model={device?.hardwareModel ?? ""}
                    updating={updating}
                    setUpdating={setUpdating}
                  />
                </Route>
                <Route
                  path={generatePath(path, { deviceId, category: "servers" })}
                  component={CallServerSettings}
                />
                <Route
                  path={generatePath(path, { deviceId, category: "network" })}
                  component={NetworkQualitySettings}
                />
                <Route component={DisplaySettings} />
              </Switch>
            </Content.ScrollingList>
          </Form>
        </BlockingForm>
      )}
    </Formik>
  );
};
